/* eslint-disable camelcase */
import { createContext, useContext, useState, useCallback } from 'react';
import { objectEquality } from 'helpers/functional';
import { useMountEffect } from 'hooks';
import LoadingCurtain from 'components/loading-curtain';
import { useGetCurrentUserLazyQuery, UserFragment } from 'graphpl/core-types';

export type User = UserFragment;

type UserContextData = {
  user: User | null;
  authenticated?: boolean;
};

export const UserContext = createContext<UserContextData>({
  user: null,
  authenticated: false,
});

type UserProviderProps = {
  initialUser: User;
  authenticated: boolean;
  disableSockets?: boolean;
};

export const UserProvider: React.FC<UserProviderProps> = ({
  children,
  initialUser,
  authenticated,
}) => {
  const [userState, setUser] = useState<User>(initialUser);

  const updateUser = useCallback(
    (update: Partial<User>) => {
      const newState = { ...userState, ...update };
      if (objectEquality(userState || {}, newState || {})) return;
      if (authenticated) setUser(newState);
    },
    [userState], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const [queryCurrentUser, { loading }] = useGetCurrentUserLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ currentUser }) => {
      updateUser(currentUser);
    },
  });

  useMountEffect(() => {
    if (initialUser === null && authenticated) queryCurrentUser();
  });

  if (loading || (authenticated && userState === null))
    return <LoadingCurtain />;

  return (
    <UserContext.Provider
      value={{
        user: userState,
        authenticated: authenticated && Boolean(userState),
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextData => useContext(UserContext);
